/**
 * useUpdateApplication Hook
 *
 * React Query mutation for updating an existing application.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { applicationService } from '../../../api/application.service';
import type {
  ApplicationSanitized,
  ApplicationUpdateDTO,
} from '../../../types/application.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseUpdateApplicationOptions {
  tenantId: string;
  applicationId: string;
  onSuccess?: (data: ApplicationSanitized) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for updating an application
 */
export const useUpdateApplication = (
  options: UseUpdateApplicationOptions
): UseMutationResult<ApplicationSanitized, Error, ApplicationUpdateDTO> => {
  const { tenantId, applicationId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<ApplicationSanitized, Error, ApplicationUpdateDTO>({
    mutationFn: async (data: ApplicationUpdateDTO) => {
      console.log('[useUpdateApplication] Updating application:', applicationId);
      return await applicationService.updateApplication(tenantId, applicationId, data);
    },
    onSuccess: (data) => {
      console.log('[useUpdateApplication] Application updated successfully:', data.applicationId);
      // Invalidate both list and single application queries
      queryClient.invalidateQueries({ queryKey: ['applications', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['applications', tenantId, applicationId] });
      // Invalidate role caches (bidirectional sync: application.role <-> role.applications)
      // This ensures Roles page and Manage Role Assignments modal show updated app assignments
      queryClient.invalidateQueries({ queryKey: ['roles', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['role', tenantId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useUpdateApplication] Failed to update application:', getErrorMessage(error));
      onError?.(error);
    },
  });
};
